import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SelectFarmWarehouse, { getAOCWarehouses } from "../../../components/SelectFarmWarehouse";
import DatePicker from "../../../shared/DatePicker";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { numberFormatErrorToZero as numberFormat } from "../../../utils/numberFunctions";

const DOZEN_SIZE = 12;

const DISPLAY_UNIT_OPTIONS = [
    { id: "doz", label: "Doz" },
    { id: "pcs", label: "Pcs" },
];

const emptyReport = {
    startDate: "",
    endDate: "",
    selectedEventCount: 0,
    inputOysters: 0,
    outputOysters: 0,
    varianceOysters: 0,
    weightedGrowth: null,
    leases: [],
};

const getDefaultStartDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date;
};

const formatApiDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return "";
    }

    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;
};

const parseDisplayDate = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const getDisplayUnitLabel = (displayUnit) => (displayUnit === "doz" ? "doz" : "pcs");

const trimToTwoDecimals = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return null;
    }

    return Math.abs(numericValue) < 0.005 ? 0 : Number(numericValue.toFixed(2));
};

const trimToThreeDecimals = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return null;
    }

    return Math.abs(numericValue) < 0.0005 ? 0 : Number(numericValue.toFixed(3));
};

const convertQuantityForDisplay = (value, displayUnit) => {
    const numericValue = Number(value) || 0;

    if (displayUnit !== "doz") {
        return trimToTwoDecimals(numericValue) || 0;
    }

    return trimToTwoDecimals(numericValue / DOZEN_SIZE) || 0;
};

const formatQuantity = (value, displayUnit) => {
    return `${numberFormat(convertQuantityForDisplay(value, displayUnit))} ${getDisplayUnitLabel(displayUnit)}`;
};

const formatGrowth = (value) => {
    const roundedValue = trimToTwoDecimals(value);

    if (roundedValue === null) {
        return "-";
    }

    const prefix = roundedValue > 0 ? "+" : "";
    return `${prefix}${numberFormat(roundedValue)}`;
};

const formatGrowthPerDay = (value) => {
    const roundedValue = trimToThreeDecimals(value);

    if (roundedValue === null) {
        return "-";
    }

    const prefix = roundedValue > 0 ? "+" : "";
    return `${prefix}${numberFormat(roundedValue)}`;
};

const formatNumber = (value) => {
    const roundedValue = trimToTwoDecimals(value);

    if (roundedValue === null) {
        return "-";
    }

    return numberFormat(roundedValue);
};

const formatDays = (value) => `${formatNumber(value)} days`;

const getTextValue = (value) => (value || "").toString().toLowerCase();

const getNumericValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : Number.NEGATIVE_INFINITY;
};

const getDateValue = (value) => {
    const dateValue = new Date(value).getTime();
    return Number.isFinite(dateValue) ? dateValue : Number.NEGATIVE_INFINITY;
};

const compareValues = (left, right) => {
    if (left < right) {
        return -1;
    }

    if (left > right) {
        return 1;
    }

    return 0;
};

const sortRows = (rows, columns, sort) => {
    const column = columns.find((item) => item.id === sort.orderBy);

    if (!column) {
        return rows;
    }

    const direction = sort.order === "desc" ? -1 : 1;
    return [...rows]
        .map((row, index) => ({ row, index }))
        .sort((left, right) => {
            const result = compareValues(column.getValue(left.row), column.getValue(right.row));
            return result === 0 ? left.index - right.index : result * direction;
        })
        .map((item) => item.row);
};

const getNextSort = (currentSort, orderBy) => ({
    orderBy,
    order: currentSort.orderBy === orderBy && currentSort.order === "asc" ? "desc" : "asc",
});

const LEASE_COLUMNS = [
    { id: "lease", label: "Lease", getValue: (lease) => getTextValue(lease.leaseDisplayName || lease.leaseName) },
    { id: "inputOysters", label: "Input", align: "right", getValue: (lease) => getNumericValue(lease.inputOysters) },
    { id: "outputOysters", label: "Output", align: "right", getValue: (lease) => getNumericValue(lease.outputOysters) },
    { id: "varianceOysters", label: "Variance", align: "right", getValue: (lease) => getNumericValue(lease.varianceOysters) },
    { id: "days", label: "Avg Days", align: "right", getValue: (lease) => getNumericValue(lease.days) },
    { id: "weightedGrowth", label: "Avg Growth", align: "right", getValue: (lease) => getNumericValue(lease.weightedGrowth) },
    { id: "averageGrowthPerDay", label: "Avg Growth / Day", align: "right", getValue: (lease) => getNumericValue(lease.averageGrowthPerDay) },
];

const LINE_COLUMNS = [
    { id: "lineNumber", label: "Line", getValue: (line) => getNumericValue(line.lineNumber) },
    { id: "eventCount", label: "Events", align: "right", getValue: (line) => getNumericValue(line.eventCount) },
    { id: "inputOysters", label: "Input", align: "right", getValue: (line) => getNumericValue(line.inputOysters) },
    { id: "outputOysters", label: "Output", align: "right", getValue: (line) => getNumericValue(line.outputOysters) },
    { id: "varianceOysters", label: "Variance", align: "right", getValue: (line) => getNumericValue(line.varianceOysters) },
    { id: "days", label: "Avg Days", align: "right", getValue: (line) => getNumericValue(line.days) },
    { id: "weightedGrowth", label: "Avg Growth", align: "right", getValue: (line) => getNumericValue(line.weightedGrowth) },
    { id: "averageGrowthPerDay", label: "Avg Growth / Day", align: "right", getValue: (line) => getNumericValue(line.averageGrowthPerDay) },
];

const DETAIL_COLUMNS = [
    { id: "eventDate", label: "Event", getValue: (detail) => getDateValue(detail.eventDate) },
    { id: "inputOysters", label: "Input", align: "right", getValue: (detail) => getNumericValue(detail.inputOysters) },
    { id: "outputOysters", label: "Output", align: "right", getValue: (detail) => getNumericValue(detail.outputOysters) },
    { id: "varianceOysters", label: "Variance", align: "right", getValue: (detail) => getNumericValue(detail.varianceOysters) },
    { id: "days", label: "Avg Days", align: "right", getValue: (detail) => getNumericValue(detail.days) },
    { id: "growth", label: "Avg Growth", align: "right", getValue: (detail) => getNumericValue(detail.growth) },
    { id: "averageGrowthPerDay", label: "Avg Growth / Day", align: "right", getValue: (detail) => getNumericValue(detail.averageGrowthPerDay) },
];

const SortableHeaderCell = ({ column, sort, onSort }) => (
    <TableCell align={column.align || "left"} sortDirection={sort.orderBy === column.id ? sort.order : false}>
        <TableSortLabel
            active={sort.orderBy === column.id}
            direction={sort.orderBy === column.id ? sort.order : "asc"}
            onClick={() => onSort(column.id)}
        >
            {column.label}
        </TableSortLabel>
    </TableCell>
);

const SummaryValue = ({ label, value }) => (
    <Grid item xs={12} sm={6} md={3}>
        <Card>
            <CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h5">{value}</Typography>
            </CardContent>
        </Card>
    </Grid>
);

const LeaseGrowthReport = ({ access }) => {
    const [farm, setFarm] = React.useState(null);
    const [startDate, setStartDate] = React.useState(getDefaultStartDate);
    const [endDate, setEndDate] = React.useState(() => new Date());
    const [allFarms, setAllFarms] = React.useState(false);
    const [allowedFarmList, setAllowedFarmList] = React.useState([]);
    const [displayUnit, setDisplayUnit] = React.useState("doz");
    const [report, setReport] = React.useState(emptyReport);
    const [expandedLeases, setExpandedLeases] = React.useState({});
    const [expandedLines, setExpandedLines] = React.useState({});
    const [leaseSort, setLeaseSort] = React.useState({ orderBy: "lease", order: "asc" });
    const [lineSorts, setLineSorts] = React.useState({});
    const [detailSorts, setDetailSorts] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        getAOCWarehouses(access, false, setAllowedFarmList);
    }, [access]);

    const canLoad = (allFarms || !!farm?.warehouseId) && startDate instanceof Date && endDate instanceof Date;

    const toggleLease = (leaseId) => {
        setExpandedLeases((current) => ({
            ...current,
            [leaseId]: !current[leaseId],
        }));
    };

    const getLineKey = (leaseId, lineId) => `${leaseId}-${lineId}`;

    const toggleLine = (leaseId, lineId) => {
        const lineKey = getLineKey(leaseId, lineId);
        setExpandedLines((current) => ({
            ...current,
            [lineKey]: !current[lineKey],
        }));
    };

    const handleLeaseSort = (orderBy) => {
        setLeaseSort((current) => getNextSort(current, orderBy));
    };

    const handleLineSort = (leaseId, orderBy) => {
        setLineSorts((current) => ({
            ...current,
            [leaseId]: getNextSort(current[leaseId] || { orderBy: "lineNumber", order: "asc" }, orderBy),
        }));
    };

    const handleDetailSort = (lineKey, orderBy) => {
        setDetailSorts((current) => ({
            ...current,
            [lineKey]: getNextSort(current[lineKey] || { orderBy: "eventDate", order: "desc" }, orderBy),
        }));
    };

    const handleSubmit = async () => {
        if (!canLoad) {
            return;
        }

        setLoading(true);
        setHasSubmitted(true);
        setError("");
        setReport(emptyReport);
        setExpandedLeases({});
        setExpandedLines({});

        try {
            const activeWarehouseId = allFarms ? undefined : farm.warehouseId;
            const data = await getAPIDataParamsReturn("/Farms/BatchReporting/LeaseGrowthReport", {
                WarehouseId: activeWarehouseId,
                StartDate: formatApiDate(startDate),
                EndDate: formatApiDate(endDate),
            });

            setReport({ ...emptyReport, ...(data || {}), leases: Array.isArray(data?.leases) ? data.leases : [] });
        }
        catch {
            setError("Unable to load the Lease Growth Report.");
            setReport(emptyReport);
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
                {!allFarms ? (
                    <SelectFarmWarehouse setState={setFarm} state={farm} access={access} />
                ) : (
                    <Typography style={{ display: "inline" }} variant="h2" mb={0}>
                        Summary of All Farms
                    </Typography>
                )}
                {allowedFarmList.length > 0 ? (
                    <ToggleButton
                        color="primary"
                        style={{ marginLeft: "20px" }}
                        selected={allFarms}
                        onChange={() => setAllFarms((current) => !current)}
                    >
                        <div style={{ whiteSpace: "nowrap" }}>All Farms</div>
                    </ToggleButton>
                ) : null}
            </Grid>

            <Grid item xs={12} md={7}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                    <Typography variant="body2" color="text.secondary">Display unit</Typography>
                    {DISPLAY_UNIT_OPTIONS.map((option) => (
                        <ToggleButton
                            key={option.id}
                            color="primary"
                            selected={displayUnit === option.id}
                            onChange={() => setDisplayUnit(option.id)}
                            value={option.id}
                        >
                            <div style={{ whiteSpace: "nowrap" }}>{option.label}</div>
                        </ToggleButton>
                    ))}
                </Stack>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Lease Growth Report" />
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker label="Start Date" date={startDate} setDate={setStartDate} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker label="End Date" date={endDate} setDate={setEndDate} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleSubmit}
                                    disabled={!canLoad || loading}
                                    sx={{ minHeight: 56 }}
                                >
                                    {loading ? "Loading..." : "Submit"}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {error ? (
                <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                </Grid>
            ) : null}

            <SummaryValue label="Input" value={formatQuantity(report.inputOysters, displayUnit)} />
            <SummaryValue label="Output" value={formatQuantity(report.outputOysters, displayUnit)} />
            <SummaryValue label="Variance" value={formatQuantity(report.varianceOysters, displayUnit)} />
            <SummaryValue label="Avg Growth" value={formatGrowth(report.weightedGrowth)} />

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Leases"
                        subheader={hasSubmitted ? `${numberFormat(report.selectedEventCount || 0)} grading events from ${parseDisplayDate(report.startDate)} to ${parseDisplayDate(report.endDate)}` : ""}
                    />
                    <CardContent>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={48} />
                                        {LEASE_COLUMNS.map((column) => (
                                            <SortableHeaderCell key={column.id} column={column} sort={leaseSort} onSort={handleLeaseSort} />
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortRows(report.leases, LEASE_COLUMNS, leaseSort).map((lease) => {
                                        const expanded = !!expandedLeases[lease.leaseId];
                                        const lineSort = lineSorts[lease.leaseId] || { orderBy: "lineNumber", order: "asc" };
                                        const sortedLines = sortRows(lease.lines || [], LINE_COLUMNS, lineSort);

                                        return (
                                            <React.Fragment key={lease.leaseId}>
                                                <TableRow hover>
                                                    <TableCell>
                                                        <IconButton size="small" onClick={() => toggleLease(lease.leaseId)}>
                                                            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>{lease.leaseDisplayName || lease.leaseName}</TableCell>
                                                    <TableCell align="right">{formatQuantity(lease.inputOysters, displayUnit)}</TableCell>
                                                    <TableCell align="right">{formatQuantity(lease.outputOysters, displayUnit)}</TableCell>
                                                    <TableCell align="right">{formatQuantity(lease.varianceOysters, displayUnit)}</TableCell>
                                                    <TableCell align="right">{formatDays(lease.days)}</TableCell>
                                                    <TableCell align="right">{formatGrowth(lease.weightedGrowth)}</TableCell>
                                                    <TableCell align="right">{formatGrowthPerDay(lease.averageGrowthPerDay)}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={8} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                                                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                            <Box sx={{ px: 2, py: 1.5 }}>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell width={48} />
                                                                            {LINE_COLUMNS.map((column) => (
                                                                                <SortableHeaderCell
                                                                                    key={column.id}
                                                                                    column={column}
                                                                                    sort={lineSort}
                                                                                    onSort={(orderBy) => handleLineSort(lease.leaseId, orderBy)}
                                                                                />
                                                                            ))}
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {sortedLines.map((line) => {
                                                                            const lineKey = getLineKey(lease.leaseId, line.lineId);
                                                                            const lineExpanded = !!expandedLines[lineKey];
                                                                            const detailSort = detailSorts[lineKey] || { orderBy: "eventDate", order: "desc" };
                                                                            const sortedDetails = sortRows(line.details || [], DETAIL_COLUMNS, detailSort);

                                                                            return (
                                                                            <React.Fragment key={lineKey}>
                                                                                <TableRow>
                                                                                    <TableCell>
                                                                                        <IconButton size="small" onClick={() => toggleLine(lease.leaseId, line.lineId)}>
                                                                                            {lineExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                                                        </IconButton>
                                                                                    </TableCell>
                                                                                    <TableCell>Line {formatNumber(line.lineNumber)}</TableCell>
                                                                                    <TableCell align="right">{numberFormat(line.eventCount || 0)}</TableCell>
                                                                                    <TableCell align="right">{formatQuantity(line.inputOysters, displayUnit)}</TableCell>
                                                                                    <TableCell align="right">{formatQuantity(line.outputOysters, displayUnit)}</TableCell>
                                                                                    <TableCell align="right">{formatQuantity(line.varianceOysters, displayUnit)}</TableCell>
                                                                                    <TableCell align="right">{formatDays(line.days)}</TableCell>
                                                                                    <TableCell align="right">{formatGrowth(line.weightedGrowth)}</TableCell>
                                                                                    <TableCell align="right">{formatGrowthPerDay(line.averageGrowthPerDay)}</TableCell>
                                                                                </TableRow>
                                                                                <TableRow>
                                                                                    <TableCell colSpan={9} sx={{ p: 0, borderBottom: lineExpanded ? undefined : 0 }}>
                                                                                        <Collapse in={lineExpanded} timeout="auto" unmountOnExit>
                                                                                            <Box sx={{ px: 2, py: 1 }}>
                                                                                                <Table size="small">
                                                                                                    <TableHead>
                                                                                                        <TableRow>
                                                                                                            {DETAIL_COLUMNS.map((column) => (
                                                                                                                <SortableHeaderCell
                                                                                                                    key={column.id}
                                                                                                                    column={column}
                                                                                                                    sort={detailSort}
                                                                                                                    onSort={(orderBy) => handleDetailSort(lineKey, orderBy)}
                                                                                                                />
                                                                                                            ))}
                                                                                                        </TableRow>
                                                                                                    </TableHead>
                                                                                                    <TableBody>
                                                                                                        {sortedDetails.map((detail) => (
                                                                                                            <TableRow key={`${lineKey}-${detail.inputGroupId}-${detail.eventDate}-${detail.inputGradeId}-${detail.outputGrades}`}>
                                                                                                                <TableCell>
                                                                                                                    <Typography variant="body2">{parseDisplayDate(detail.eventDate)} | Group {numberFormat(detail.inputGroupId || 0)}</Typography>
                                                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                                                        {detail.inputGrade} to {detail.outputGrades || "Unknown"}
                                                                                                                    </Typography>
                                                                                                                </TableCell>
                                                                                                                <TableCell align="right">{formatQuantity(detail.inputOysters, displayUnit)}</TableCell>
                                                                                                                <TableCell align="right">{formatQuantity(detail.outputOysters, displayUnit)}</TableCell>
                                                                                                                <TableCell align="right">{formatQuantity(detail.varianceOysters, displayUnit)}</TableCell>
                                                                                                                <TableCell align="right">{formatDays(detail.days)}</TableCell>
                                                                                                                <TableCell align="right">{formatGrowth(detail.growth)}</TableCell>
                                                                                                                <TableCell align="right">{formatGrowthPerDay(detail.averageGrowthPerDay)}</TableCell>
                                                                                                            </TableRow>
                                                                                                        ))}
                                                                                                    </TableBody>
                                                                                                </Table>
                                                                                            </Box>
                                                                                        </Collapse>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            </React.Fragment>
                                                                            );
                                                                        })}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                    {report.leases.length === 0 && !loading ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {hasSubmitted ? "No lease growth records found." : "No report loaded."}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default LeaseGrowthReport;